import { useEffect, useState } from "react";
import { Rocket, Check, EyeOff, Lock } from "lucide-react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";

export default function FeatureRollout() {
  const [modules, setModules] = useState([]);
  const [hidden, setHidden] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/module-rollout");
      setModules(data.modules || []);
      setHidden(new Set(data.hidden_modules || []));
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || "Failed to load"); }
  };
  useEffect(() => { load(); }, []);

  const toggle = (key, locked) => {
    if (locked) return;
    setHidden((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const save = async () => {
    setBusy(true); setErr("");
    try {
      await api.put("/admin/module-rollout", { hidden_modules: Array.from(hidden) });
      toast.success("Feature rollout updated. Changes apply globally to all tenants.");
      load();
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || "Failed to save"); }
    finally { setBusy(false); }
  };

  const offlineCount = modules.filter((m) => hidden.has(m.key) && !m.locked).length;

  return (
    <div className="vb-fade-in" data-testid="feature-rollout-page">
      <div className="flex items-center gap-3 mb-2">
        <Rocket size={26} style={{ color: "var(--vb-accent)" }} />
        <h1 className="vb-heading text-3xl" style={{ color: "var(--vb-text-2)" }}>Feature Rollout</h1>
      </div>
      <p className="opacity-70 mb-6 max-w-3xl">
        Control which modules are live across <strong>all tenants</strong>. Modules toggled <b>Offline</b>
        are hidden everywhere (navigation, pages, reports, onboarding) and their APIs are blocked — no data
        is deleted, so you can bring them back online in phases at any time.
      </p>

      {err && <div className="vb-pill err mb-4" data-testid="rollout-error">{err}</div>}

      <div className="vb-card overflow-hidden mb-6">
        <table className="vb-table">
          <thead><tr><th>Module</th><th>Description</th><th style={{ textAlign: "center" }}>Status</th></tr></thead>
          <tbody>
            {modules.map((m) => {
              const isHidden = hidden.has(m.key);
              return (
                <tr key={m.key} data-testid={`rollout-row-${m.key}`}>
                  <td><strong>{m.label}</strong></td>
                  <td className="opacity-70 text-sm" style={{ maxWidth: 420 }}>{m.description}</td>
                  <td style={{ textAlign: "center" }}>
                    {m.locked ? (
                      <span className="vb-pill" title="Essential — always online"><Lock size={12} /> Always on</span>
                    ) : (
                      <button
                        className={`vb-pill ${isHidden ? "err" : "ok"}`}
                        style={{ cursor: "pointer" }}
                        onClick={() => toggle(m.key, m.locked)}
                        data-testid={`rollout-toggle-${m.key}`}
                        title="Click to toggle online/offline"
                      >
                        {isHidden ? <><EyeOff size={12} /> Offline</> : <><Check size={12} /> Online</>}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-4">
        <button className="vb-btn" onClick={save} disabled={busy} data-testid="rollout-save">
          {busy ? "Saving…" : "Save changes"}
        </button>
        <span className="opacity-70 text-sm" data-testid="rollout-offline-count">
          {offlineCount} module{offlineCount === 1 ? "" : "s"} currently offline
        </span>
      </div>
    </div>
  );
}
